import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation, AfterViewInit } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { flattenDeep, forEach, isNil, lowerCase, sortBy, trim } from 'lodash';
import { Observable, Subject, Subscription, of, throwError } from 'rxjs';
import { catchError, first, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { AzureBlobStorageService } from '@app/shared/services/api/azure-blob-storage.service';
import { DisclaimerService } from '@app/shared/services/api/disclaimer.service';
import { UserService } from '@app/shared/services/api/user.service';
import { AuthService } from '@app/shared/services/auth.service';
import { HeaderFooterVisibilityService } from '@app/shared/services/header-footer-visibility.service';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { UserRegistrationRequestService } from '@app/registration-request/services/user-registration-request.service';
import { Region } from '@app/shared/services/api/regions.service';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { UserRegionService, UserRegionMunicipalitites, RegionMunicipality } from '@app/shared/services/api/user-regions.service';
import { Item, ListTreeComponent, Node } from '@app/shared/components/list-tree/list-tree.component';
import { NotificationDialogComponent } from '@app/shared/dialog/notification-dialog/notification-dialog.component';
import { isEmailValid, saveFile } from '@app/shared/utility';
import { cssVariables } from '@env/css-vars';
import { environment } from '@env/environment';
import { GemisTenant, IdpName, Roles, UserStatus, ExternalRoles } from '@app/shared/enums';
import { genderOptions, genderOptionsGemis, userTitles } from '@app/shared/constants';
import { JobFunction, Role, User } from '@app/shared/models/user';
import { Municipality } from '@app/shared/services/api/municipality.service';
import { MtcaptchaService } from '@app/shared/services/mtcaptcha.service';
import { CommunesDisplay, RegionAssignments, RoleOptions, UserRegistrationDetails } from '@app/public/models/user-registration';

@Component({
  selector: 'app-user-registration',
  templateUrl: './user-registration.component.html',
  styleUrls: ['./user-registration.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class UserRegistrationComponent implements OnInit, OnDestroy, AfterViewInit {
  private ngUnsubscribe$ = new Subject<void>();
  @ViewChild('tree') tree: ListTreeComponent | undefined;

  isDsoToDsoUser: boolean = false;
  isLoading: boolean = true;
  isRegistrationDone: boolean = false;
  hasChildNodes: boolean = false;

  isGenderDropdownOpen: boolean = false;
  isTitleDropdownOpen: boolean = false;
  isJobFunctionDropdownOpen: boolean = false;
  isRoleDropdownOpen: boolean = false;
  isMunicipalityDropdownOpen: boolean = false;
  isAdminAreaRegionDropdownOpen: boolean = false;

  idpName?: IdpName;
  latestLegalVersion: string = '';
  municipalityId: string = '';
  sender: string = 'Team EnergiePortal';
  senderEmail: string = '';
  tenant: string = '';
  userGuid: string = '';
  userRole: string = '';
  emailErrorMessage: string = '';

  vScrollHeightPx: number = 40;

  mtcaptcha: string = 'mtcaptcha-verifiedtoken';
  mtcaptchaToken: string = '';
  captchaValidated: boolean = false;
  captchaResolved: boolean = false;
  hiddenInput!: HTMLInputElement;

  regionMunicipalities: RegionMunicipality[] = [];
  showCheckBox = false;

  fontSize: string = 'var(--ep-font-size-body-sm)';

  genderOptions = {};
  userTitleOptions = {};
  jobFunctionOptions: JobFunction[] = [];
  municipalityOptions: Municipality[] | Region[] = [];
  kommunenOptions: CommunesDisplay[] = [];
  selectedMunicipality!: Partial<Municipality | Region>;
  selectedCommunes!: Partial<Municipality>;
  rolesOptions: RoleOptions[] = [];
  regions: Node[] = [];
  isEmailVerifying!: boolean;

  registrationForm: FormGroup = this._formBuilder.group({
    firstName: new FormControl('', [Validators.required]),
    lastName: new FormControl('', [Validators.required]),
    phoneNumber: new FormControl('', [Validators.required]),
    email: new FormControl('', {
      updateOn: 'blur',
      asyncValidators: [this.compareCurrentUserEmail()],
    }),
    jobFunction: new FormControl('', [Validators.required]),
    jobFunctionInfo: new FormControl('', [Validators.maxLength(250)]),
    department: new FormControl('', [Validators.maxLength(250)]),
    comment: new FormControl('', [Validators.maxLength(500)]),
    allowConditions: new FormControl(false, [Validators.required]),
    allowNewsletter: new FormControl(false),
    role: new FormControl('', [Validators.required]),
    title: new FormControl(''),
    gender: new FormControl(null),
  });

  public selectedNewCommunes: CommunesDisplay[] = [];

  get isGemisDSO(): boolean {
    return Object.values(GemisTenant).includes(environment.tenant as GemisTenant);
  }

  get isExternalUser(): boolean {
    return (this.idpName?.toUpperCase() === IdpName.CIAM && Object.values(ExternalRoles).includes(this.role.roleCode as ExternalRoles)) || !this.userGuid;
  }

  get role(): RoleOptions {
    return this.registrationForm.get('role')?.value;
  }

  get municipality(): Municipality {
    return this.registrationForm.get('municipality')?.value;
  }

  get communes(): Municipality {
    return this.registrationForm.get('communes')?.value;
  }

  constructor(
    private _formBuilder: FormBuilder,
    private _headerFooterVisibilityService: HeaderFooterVisibilityService,
    private _userService: UserService,
    private _route: ActivatedRoute,
    private _router: Router,
    public translate: TranslateService,
    public authService: AuthService,
    private matDialog: MatDialog,
    private overlayLoader: OverlayLoaderService,
    private blobStorageService: AzureBlobStorageService,
    private overLoaderService: OverlayLoaderService,
    private disclaimerService: DisclaimerService,
    private registrationRequestService: UserRegistrationRequestService,
    private userMunicipalitiesService: UserMunicipalitiesService,
    private userRegionService: UserRegionService,
    private mtcaptchaService: MtcaptchaService
  ) {}

  public ngOnInit(): void {
    this.resetKommunenOptions();
    this.userGuid = this._route.snapshot.params['id'];
    this._headerFooterVisibilityService.isHeaderVisible = true;
    this._headerFooterVisibilityService.isFooterVisible = true;
    this.tenant = environment.tenant.trim().toLowerCase();
    this.senderEmail = environment.companyGroupEmail;
    this.setLatestLegalVersion();
    this.setupTranslations();
    this.fetchJobFunctions();
    this.isGemisDSO ? this.initializeGemis() : this.initializeNonGemis();
    this.setControlOptions();
  }

  public ngAfterViewInit(): void {
    this.observeHiddenInput();
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.unsubscribe();
  }

  public register(): void {
    const { role, ...registrationForm } = this.registrationForm.value;
    const payload = {
      ...registrationForm,
      ...role,
      phoneNumber: this._removeTrailingSpaces(this.registrationForm.value.phoneNumber),
      legalDisclaimerVersion: this.latestLegalVersion,
      userId: this.registrationForm.value.kid ?? '',
      idpName: this.idpName,
    };

    this.overlayLoader.isLoading = true;

    let result$;

    if (this.isGemisDSO && this.isExternalUser) {
      result$ = this.gemisExternalRegister(payload);
    } else if (this.isGemisDSO && !this.isExternalUser) {
      result$ = this.gemisInternalRegister(payload);
    } else {
      result$ = this._userService.registerUser(this.userGuid, payload);
    }

    result$.pipe(first()).subscribe({
      next: () => this.handleSuccessfulRegistration(),
      error: () => this.handleFailedRegistration(),
    });
  }

  public onMunicipalitySelectionChanged(selectedKommune: Municipality | Region): void {
    this.selectedMunicipality = selectedKommune;
    if (this.role && this.role.roleCode === 'AAW' && selectedKommune?.guid) {
      this.setupCommuneOptions(selectedKommune.guid);
    }
    if (selectedKommune) {
      this.userMunicipalitiesService.updateMunicipality(selectedKommune as any);
    }

    this.registrationForm.get('municipality')?.markAsDirty();
  }

  public onCommunesSelectionChanged(selectedCommunesArray: CommunesDisplay[]): void {
    this.selectedNewCommunes = selectedCommunesArray;
  }

  public downloadFile(): Subscription {
    this.overLoaderService.isLoading = true;

    return this.blobStorageService
      .downloadFileDirect(`downloadPdf/downloadpdf.pdf`)
      .pipe(first())
      .subscribe((data: Blob) => {
        saveFile(data, 'Allgemeine Nutzungsbedingungen.pdf');
        this.overLoaderService.isLoading = false;
      });
  }

  public mailer(): void {
    const mail = this.senderEmail;
    window.location.href = `mailto:${mail}`;
  }

  public removeUserInputSpaces(controlName: string): void {
    const inputNumber = this.registrationForm.controls[controlName];

    if (inputNumber) {
      const cleanedInputNumber = this._removeTrailingSpaces(inputNumber.value);

      inputNumber.setValue(cleanedInputNumber);
    }
  }

  public redirectToDataProtection(): void {
    window.open(environment.dataProtectionUrl, '_blank');
  }

  public handleSuccessfulRegistration(): void {
    this.overlayLoader.isLoading = false;
    this.isRegistrationDone = true;
    this._headerFooterVisibilityService.isFooterVisible = false;
    const route = this.idpName?.toUpperCase() === IdpName.CIAM.toUpperCase() ? '/emailsent' : '/registersuccess';
    this._router.navigateByUrl(route);
  }

  public handleFailedRegistration(): void {
    this.overlayLoader.isLoading = false;
    this.showErrorMessage('Benutzer konnte nicht registriert werden.');
  }

  public showErrorMessage(message: string): void {
    this.matDialog.open(NotificationDialogComponent, {
      data: {
        descriptionText: message,
        descriptionFaClass: 'fa-exclamation-triangle fa-2x',
        descriptionFaColor: cssVariables['--ep-error-color'],
      },
      position: { top: '10%' },
      panelClass: ['-md'],
    });
  }

  public onDropdownToggle(dropdown: string): void {
    switch (dropdown) {
      case 'gender':
        this.isGenderDropdownOpen = !this.isGenderDropdownOpen;
        break;
      case 'title':
        this.isTitleDropdownOpen = !this.isTitleDropdownOpen;
        break;
      case 'jobFunction':
        this.isJobFunctionDropdownOpen = !this.isJobFunctionDropdownOpen;
        break;
      case 'role':
        this.isRoleDropdownOpen = !this.isRoleDropdownOpen;
      case 'municipality':
        this.isMunicipalityDropdownOpen = !this.isMunicipalityDropdownOpen;
      case 'adminAreaRegion':
        this.isAdminAreaRegionDropdownOpen = !this.isAdminAreaRegionDropdownOpen;
    }
  }

  public isFieldInvalid(controlName: string): boolean {
    const control = this.registrationForm.get(controlName);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  private gemisExternalRegister(registrationForm: any): Observable<void | null> {
    const isMunicipalityWorker = registrationForm.roleCode.toUpperCase() === Roles.municipalityWorker;
    const gemisUser = {
      ...registrationForm,
      idpName: IdpName.CIAM,
    };

    const userPayload = isMunicipalityWorker
      ? {
          ...gemisUser,
          assignedMunicipalityId: registrationForm.municipality.guid,
          municipalities: [registrationForm.municipality],
        }
      : {
          ...gemisUser,
          municipalities: registrationForm.communes.map((municipality: Municipality) => ({
            ...municipality,
            municipalityKey: municipality.key,
          })),
        };

    return this.validateCaptcha().pipe(
      switchMap(() => this._userService.registerGemisUser(userPayload)),
      switchMap((user: User) =>
        isMunicipalityWorker
          ? of(null)
          : this._userService.registerUserRegion({
              ...user,
              rolesCode: gemisUser.roleCode,
              regionId: registrationForm.municipality.guid,
            })
      )
    );
  }

  private gemisInternalRegister(registrationForm: any): Observable<void | null> {
    let userPayload = {
      ...registrationForm,
      idpName: IdpName.AAD,
      assignedMunicipalityId: '',
      municipalities: [],
      roleId: this.userRole,
      guid: this.userGuid,
      roleCode: 'eon_internal',
    };

    return this.validateCaptcha().pipe(
      switchMap(() => this._userService.registerGemisUser(userPayload)),
      switchMap(() => of(null))
    );
  }

  private initializeNonGemis(): void {
    this._userService
      .getRegistrationDetails(this.userGuid)
      .pipe(
        take(1),
        switchMap((data: UserRegistrationDetails) => {
          const canActivateUser = isNil(data.registrationDate) && data.status === UserStatus.activationPending && isNil(data.userId);
          return canActivateUser ? of(data) : throwError(() => new Error('Cannot activate user'));
        }),
        tap((data: UserRegistrationDetails) => {
          this.registrationForm.patchValue({
            allowNewsletter: false,
            firstName: data.firstName,
            lastName: data.lastName,
            phoneNumber: data.phoneNumber,
            email: data.email,
            role: {
              roleName: data.roleName,
            },
          });

          this.idpName = data.idpName;
          this.userRole = data.roleId;
          this.setListTreeProperties(data.assingments);

          if (data.idpName.toUpperCase() === IdpName.CIAM.toUpperCase()) {
            this.registrationForm.addControl('acceptTerms', new FormControl(false, [Validators.required]));
          }
          if (data.idpName.toUpperCase() === IdpName.AAD.toUpperCase()) {
            this.registrationForm.addControl('kid', new FormControl(data.userId, [Validators.required]));
          }

          if (trim(lowerCase(data.roleCode)) === Roles.municipalityWorker.toLowerCase() && data.assignedMunicipalityId) {
            this.municipalityId = data.assignedMunicipalityId;
          }
          if (data.roleCode === Roles.technicalDsoExpert || data.roleCode === Roles.technicalDsoEmployee) {
            this.isDsoToDsoUser = true;
            this.registrationForm.controls.allowConditions.setValue(true);
          }
        })
      )
      .subscribe({
        next: () => (this.isLoading = false),
        error: () => (this.isLoading = false),
      });
  }

  private initializeGemis(): void {
    if (this.isExternalUser) {
      this.idpName = IdpName.CIAM;
      this.registrationForm.addControl('municipality', new FormControl('', [Validators.required]));
      this.registrationForm.addControl('acceptTerms', new FormControl(false, [Validators.required]));
      this.setupRoleOptions();
      this.setupMunicipalityOptions();
    }

    this.mtcaptchaService.initializeMTCaptcha();
    this.mtcaptchaService
      .getHiddenValue$(this.mtcaptcha)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((value) => {
        this.mtcaptchaToken = value;
        this.checkSubmitEnabled(value);
      });
    this.observeHiddenInput();
  }

  private setupMunicipalityOptions(): void {
    this.registrationForm.controls.role.valueChanges
      .pipe(
        takeUntil(this.ngUnsubscribe$),
        map((value: RoleOptions) => {
          if (value.roleCode?.toLowerCase() === Roles.municipalityWorker.toLowerCase()) {
            return this.mapMunicipalitiesToOptions(this.registrationRequestService.municipalities$.value);
          } else {
            return this.mapRegionsToOptions(this.registrationRequestService.regions$.value);
          }
        })
      )
      .subscribe((processedOptionsList: Region[] | Municipality[]) => {
        this.municipalityOptions = processedOptionsList;

        this.selectedMunicipality = {};
        this.registrationForm.get('municipality')?.setValue('');
        this.userMunicipalitiesService.updateMunicipality({
          guid: '',
          name: '',
          key: '',
          postCode: '',
          isDeactivated: false,
          isActive: true,
        });
      });
  }

  private mapMunicipalitiesToOptions(municipalities: Municipality[]): Municipality[] {
    if (!municipalities) {
      return [];
    }
    return municipalities.map((municipality: Municipality) => {
      let namePart = municipality.name || '';
      let keyPart = municipality.municipalityKey || municipality.key || null;

      let compositeLabel = namePart;
      if (keyPart) {
        compositeLabel += ` (${keyPart})`;
      }

      return {
        ...municipality,
        compositeLabel: compositeLabel,
      };
    });
  }

  private mapRegionsToOptions(regions: Region[]): Region[] {
    if (!regions) {
      return [];
    }

    return regions
      .filter((region: Region) => {
        if (!this.isGemisDSO) {
          return true;
        }

        const name = region.name?.trim().toLowerCase() ?? '';
        return !(name.startsWith('virtueller gemeindeverband') || name.includes('unassigned'));
      })
      .map((region: Region) => ({
        ...region,
        compositeLabel: region.name || '',
      }));
  }

  private setupCommuneOptions(regionGuid: string): void {
    this.overLoaderService.isLoading = true;
    let communesControl = this.registrationForm.get('communes');

    if (!communesControl) {
      communesControl = new FormControl([], [Validators.required]);
      this.registrationForm.addControl('communes', communesControl);
    } else {
      communesControl.setValue([]);
      communesControl.setValidators([Validators.required]);
      communesControl.markAsPristine();
      communesControl.markAsUntouched();
      communesControl.updateValueAndValidity();
    }

    this.getUserRegions(regionGuid)
      .pipe(
        takeUntil(this.ngUnsubscribe$),
        map((municipalities: RegionMunicipality[]) =>
          municipalities.map((municipality) => ({
            name: municipality.name,
            key: municipality.municipalityKey,
            isSelected: true,
            compositeLabel: `${municipality.name} (${municipality.municipalityKey})`,
          }))
        )
      )
      .subscribe((kommunenList: CommunesDisplay[]) => {
        this.kommunenOptions = kommunenList;
        if (kommunenList && kommunenList.length > 0) {
          communesControl?.setValue([...kommunenList]);
        } else {
          communesControl?.setValue([]);
        }
        this.overLoaderService.isLoading = false;

        this.userMunicipalitiesService.updateMunicipality({
          guid: '',
          name: '',
          key: '',
          postCode: '',
          isDeactivated: false,
          isActive: true,
        });
      });
  }

  private resetKommunenOptions(): void {
    this.registrationForm
      .get('role')
      ?.valueChanges.pipe()
      .subscribe(() => {
        this.kommunenOptions = [];
      });
  }

  private setupTranslations(): void {
    this.translate
      .get('module.public.registration.telefonRegex')
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((result: string) => {
        this.registrationForm.get('phoneNumber')?.setValidators([Validators.required, Validators.pattern(result)]);
      });
  }

  private setLatestLegalVersion(): void {
    const legalVersion = this.disclaimerService.getLatestDisclaimerVersionDirect();

    legalVersion.pipe(first()).subscribe((res) => {
      this.latestLegalVersion = res.version;
    });
  }

  private fetchJobFunctions(): void {
    this._userService
      .getJobFunctions()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((jobs) => (this.jobFunctionOptions = jobs));
  }

  private setListTreeProperties(regions: RegionAssignments[]): void {
    const parents = regions.map((region) => region.key);
    const children = regions.map((region) => region.value);
    const flatList = flattenDeep([parents, ...children]);
    this.hasChildNodes = children.filter((a: string[]) => a.length > 0).length > 0;
    this.vScrollHeightPx = this.vScrollHeightPx * flatList.length;
    this.vScrollHeightPx = this.vScrollHeightPx > 135 ? 135 : this.vScrollHeightPx;
    this.regions = this._generateRegionsTree(regions);
  }

  private setupRoleOptions(): void {
    this._userService
      .getRoles()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((roles) => {
        this.isLoading = false;
        this.rolesOptions = roles.map((role: Role) => ({
          roleId: role.guid,
          roleCode: role.code,
          roleName: role.name,
        }));
      });
  }

  private _generateRegionsTree(assignments: RegionAssignments[]): Node[] {
    return sortBy(
      assignments.map((b: RegionAssignments) => {
        const treeNode = {} as Node;
        treeNode.item = {} as Item;
        treeNode.item.id = b.key;
        treeNode.item.name = b.key;
        treeNode.item.selectable = false;
        treeNode.children = sortBy(
          b.value.map((c: any) => {
            const childNode = {} as Node;
            childNode.item = {} as Item;
            childNode.item.id = c;
            childNode.item.name = c;
            childNode.item.selectable = false;
            return childNode;
          }),
          [
            function (c) {
              return c.item.name;
            },
          ]
        );
        return treeNode;
      }),
      [
        function (o) {
          return o.item.name;
        },
      ]
    );
  }

  private _removeTrailingSpaces(string: string): string {
    let result = '';
    const splitString = string.split(' ');

    forEach(splitString, (str) => {
      const ss = str.trim();
      if (!isNil(ss) && ss !== '') {
        result = result + ss + ' ';
      }
    });
    return result.replace(/\s+/g, '').trim();
  }

  private getUserRegions(regionGuid: string): Observable<RegionMunicipality[]> {
    return this.userRegionService.getMunicipalitiesByRegionIdDirect(regionGuid).pipe(
      takeUntil(this.ngUnsubscribe$),
      map((municipalities: UserRegionMunicipalitites[]) => municipalities.map((item) => item.municipality).sort((a, b) => a.name.localeCompare(b.name))),
      catchError(() => of([]))
    );
  }

  private compareCurrentUserEmail(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      if (control.value.length <= 0) {
        this.translate.get('module.public.registration.validationEmailAddressRequiredText').subscribe((text: string) => {
          this.emailErrorMessage = text;
        });
        return of({
          required: true,
        });
      } else if (!isEmailValid(control.value.trim())) {
        this.emailErrorMessage = this.translate.instant('module.public.registration.validationEmailAddressRequiredText');
        return of({
          incorrectEmail: true,
        });
      } else if (!this.isGemisDSO || !this.isExternalUser) {
        return of(null);
      } else {
        this.isEmailVerifying = true;
        return this._userService.isExistingUser(control.value).pipe(
          map((exists) => {
            if (exists) {
              this.isEmailVerifying = false;
              this.emailErrorMessage = this.translate.instant('module.public.registration.emailAlreadyUsedText');
              return of({ incorrectEmail: true });
            } else {
              this.isEmailVerifying = false;
              return null;
            }
          })
        );
      }
    };
  }

  private setControlOptions(): void {
    this.userTitleOptions = userTitles;
    this.genderOptions = this.isGemisDSO ? genderOptionsGemis : genderOptions;
  }

  /* MTCaptcha Implementation */
  private observeHiddenInput(): void {
    const observer = new MutationObserver(() => {
      this.hiddenInput = document.querySelector(`input[name="${this.mtcaptcha}"]`) as HTMLInputElement;
      if (this.hiddenInput) {
        this.updateValueFromInput(this.hiddenInput);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  private checkSubmitEnabled(value: string): void {
    if (value && value.trim() !== '') {
      this.captchaResolved = true;
    } else {
      this.captchaResolved = false;
    }
  }

  private updateValueFromInput(hiddenInput: HTMLInputElement): void {
    this.mtcaptchaService.setHiddenValue(this.mtcaptcha, hiddenInput.value);

    hiddenInput.addEventListener('change', () => {
      this.mtcaptchaService.setHiddenValue(this.mtcaptcha, hiddenInput.value);
    });

    const valueObserver = new MutationObserver(() => {
      const currentValue = hiddenInput.value;
      this.mtcaptchaService.setHiddenValue(this.mtcaptcha, currentValue);
    });

    valueObserver.observe(hiddenInput, {
      attributes: true,
      attributeFilter: ['value'],
    });
  }

  private validateCaptcha(): Observable<void> {
    if (this.mtcaptchaToken) {
      try {
        return this.mtcaptchaService.callValidateToken(this.mtcaptchaToken).pipe(
          map((captchaResponse) => {
            if (captchaResponse.success) {
              this.setCaptchaVerifiedToken(captchaResponse);
              return captchaResponse.success;
            } else {
              return throwError(() => new Error('Captcha validation failed'));
            }
          })
        );
      } catch {
        return throwError(() => new Error('Captcha validation failed'));
      }
    }
    return throwError(() => new Error('Captcha validation failed'));
  }

  private setCaptchaVerifiedToken(response: any): void {
    const hiddenInput = document.getElementById('mtcaptcha-verifiedtoken') as HTMLInputElement;
    if (response.TokenInfo && response.TokenInfo.TokID) {
      hiddenInput.value = response.TokenInfo.TokID;
    }
  }
}
